import React from "react";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import {
    resolveAppSiteEntry,
    resolvePublicAppSupportUrl,
    resolvePublicLegalUrl,
    resolvePublicMarketingUrl,
} from "../../config/appSiteLinks";

const linkSx = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 1,
    py: 1.25,
    px: 0.25,
    color: "inherit",
    textDecoration: "none",
    borderBottom: "1px solid rgba(15, 61, 54, 0.1)",
    "&:last-child": { borderBottom: "none" },
    "&:hover": { color: "#059669" },
};

function LegalLinkRow({ href, label }) {
    return (
        <Link href={href} target="_blank" rel="noopener noreferrer" sx={linkSx}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {label}
            </Typography>
            <OpenInNewIcon sx={{ fontSize: 16, opacity: 0.55, flexShrink: 0 }} />
        </Link>
    );
}

/**
 * Support / Marketing / Privacy / Terms links for mobile app settings (App Store support URL).
 * @param {string} appId — from `APP_SITE_ID` in config/appSiteLinks.js
 */
export default function MobileAppLegalLinks({ appId }) {
    const entry = resolveAppSiteEntry(appId);
    if (!entry) return null;

    const supportUrl = resolvePublicAppSupportUrl(appId, { platformApp: true });
    const marketingUrl = resolvePublicMarketingUrl(appId, { platformApp: true });
    const privacyUrl = resolvePublicLegalUrl(appId, "privacy", { platformApp: true });
    const termsUrl = resolvePublicLegalUrl(appId, "terms", { platformApp: true });

    return (
        <Box sx={{ mt: 2.5 }}>
            <Typography
                variant="overline"
                sx={{
                    display: "block",
                    letterSpacing: 1.2,
                    fontWeight: 700,
                    color: "rgba(15, 61, 54, 0.55)",
                    mb: 0.5,
                }}
            >
                Help & legal
            </Typography>
            <Typography variant="caption" sx={{ display: "block", color: "rgba(15, 61, 54, 0.55)", mb: 1 }}>
                {entry.displayName} on Puzzle Interact
            </Typography>
            <Box
                sx={{
                    borderRadius: 2,
                    bgcolor: "rgba(255, 255, 255, 0.92)",
                    border: "1px solid rgba(15, 61, 54, 0.12)",
                    px: 1.25,
                }}
            >
                <LegalLinkRow href={supportUrl} label="Support" />
                <LegalLinkRow href={marketingUrl} label="Marketing" />
                <LegalLinkRow href={privacyUrl} label="Privacy Policy" />
                <LegalLinkRow href={termsUrl} label="Terms of Service" />
            </Box>
        </Box>
    );
}
